import { toast } from '@/components/ui/use-toast';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import React from 'react';
import { ToastAction } from '@/components/ui/toast';

export interface NotificationProps {
  title: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'destructive';
}

export const showNotification = ({ title, message, type }: NotificationProps) => {
  const variant = type === 'error' || type === 'destructive' ? 'destructive' : 'default';
  
  toast({
    title,
    description: message,
    variant,
  });
};

export interface DuplicateEntryNotificationProps {
  entityType: 'product' | 'category';
  name: string;
  onEdit?: () => void;
}

/**
 * Shows an enhanced notification specifically for duplicate entries
 * with a more prominent styling and helpful action
 */
export const showDuplicateEntryNotification = ({ 
  entityType, 
  name,
  onEdit 
}: DuplicateEntryNotificationProps) => {
  const titleText = 'Duplicate Name Error';
  const message = `A ${entityType} with the name "${name}" already exists. Please use a different name.`;
  
  toast({
    title: titleText,
    description: (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <span className="font-medium">{titleText}</span>
        </div>
        <p>{message}</p>
        <div className="bg-red-50 border-l-4 border-red-600 p-3 rounded text-sm">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-800">Each {entityType} must have a unique name</p>
              <p className="text-red-700 mt-1">Try adding a different qualifier or identifier to make this name unique.</p>
            </div>
          </div>
        </div>
      </div>
    ),
    variant: "destructive",
    duration: 6000, // Show for longer than default
    action: onEdit ? (
      <ToastAction altText="Edit Name" onClick={onEdit}>
        Edit Name
      </ToastAction>
    ) : undefined,
  });
}; 